import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import '../../assets/css/findLocation.scss';
import { useTranslation } from '../../contexts/TranslationContext';

const FindLocation = ({
    apiKey,
    defaultLocation = null, // Default to center of United States
    addressInput, // Initial address to populate input field
    onLocationSelect = () => { },
    placeholder = "Search for a location...",
    height = "400px",
    skipInitialMap = false // If true, don't initialize map/marker until location is selected
}) => {
    const { language } = useTranslation();

    // State for map, marker, and location
    const [map, setMap] = useState(null);
    const [marker, setMarker] = useState(null);
    const [location, setLocation] = useState(null);
    const [isReset, setIsReset] = useState(false);
    const [matchState, setMatchState] = useState(null); // eslint-disable-line no-unused-vars
    const [matchCounty, setMatchCounty] = useState(null); // eslint-disable-line no-unused-vars
    const [address, setAddress] = useState(); // Address input field
    const [currentEditAddress, setCurrentEditAddress] = useState(addressInput || ""); // Current address being edited
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [pendingSelection, setPendingSelection] = useState(false);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [isChangingLocation, setIsChangingLocation] = useState(false);

    // Google API services
    const [autocompleteSuggestion, setAutocompleteSuggestion] = useState(null);
    const [placesService, setPlacesService] = useState(null);
    const [geocoder, setGeocoder] = useState(null); // eslint-disable-line no-unused-vars

    const defaultPosition = { lat: 39.8283, lng: -98.5795 }; // Center of United States

    // Refs
    const mapRef = useRef(null);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);

    useEffect(() => {
        if (selectedPlace)
            setIsChangingLocation(true);

    }, [selectedPlace]);

    // Load Google Maps API
    useEffect(() => {
        if (!apiKey) {
            console.error('Google Maps API key is required');
            return;
        }

        // Check if API is already loaded
        if (window.google && window.google.maps) {
            initializeMap();
            return;
        }

        // Check if script is already being loaded or exists
        const existingScript = document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`);
        if (existingScript) {
            // Script already exists, wait for it to load
            if (window.google && window.google.maps) {
                initializeMap();
            } else {
                // Add listener to existing script
                const handleLoad = () => {
                    if (window.google && window.google.maps) {
                        initializeMap();
                    }
                };
                existingScript.addEventListener('load', handleLoad);

                return () => {
                    existingScript.removeEventListener('load', handleLoad);
                };
            }
            return;
        }

        // Load Google Maps API script
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initializeMap;
        document.head.appendChild(script);

        return () => {
            // Clean up script if component unmounts before loading
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, [apiKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Initialize Map and services once API is loaded
    const initializeMap = useCallback(() => {
        if (!mapRef.current || !window.google) return;

        // Initialize geocoder first
        const newGeocoder = new window.google.maps.Geocoder();
        setGeocoder(newGeocoder);

        // If skipInitialMap is true and no valid location provided, don't initialize map/marker yet
        if (skipInitialMap && (!defaultLocation || (defaultLocation.lat === 39.8283 && defaultLocation.lng === -98.5795))) {
            // Initialize services only, wait for user to select location
            setAutocompleteSuggestion(new window.google.maps.places.AutocompleteService());

            // Create map but don't show marker until location is selected
            const newMap = new window.google.maps.Map(mapRef.current, {
                center: { lat: 39.8283, lng: -98.5795 }, // Default US center
                zoom: 4, // Zoomed out
                mapTypeId: window.google.maps.MapTypeId.ROADMAP,
                mapTypeControl: true,
                streetViewControl: true,
                fullscreenControl: true,
                zoomControl: true
            });

            setMap(newMap);
            setPlacesService(new window.google.maps.places.PlacesService(newMap));
            return;
        }

        // Create new map instance
        const newMap = new window.google.maps.Map(mapRef.current, {
            center: defaultLocation,
            zoom: 15,
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true
        });

        // Create marker
        const newMarker = new window.google.maps.Marker({
            position: defaultLocation,
            map: newMap,
            draggable: true,
            animation: window.google.maps.Animation.DROP
        });

        // Add marker drag event
        newMarker.addListener('dragend', () => {
            const position = newMarker.getPosition();
            const newLocation = {
                lat: position.lat(),
                lng: position.lng()
            };
            setLocation(newLocation);
            setPendingSelection(true);

            // Get address data from coordinates using the local geocoder instance
            newGeocoder.geocode({ location: newLocation }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    let state = results[0].address_components.find(comp => comp.types.includes('administrative_area_level_1'));
                    setMatchState(state);
                    let county = results[0].address_components.find(comp => comp.types.includes('administrative_area_level_2'));
                    setMatchCounty(county);

                    setAddress(results[0].formatted_address);
                    setCurrentEditAddress(results[0].formatted_address);
                    setSelectedPlace(results[0]);
                } else {
                    setAddress('Unknown location');
                    setCurrentEditAddress('Unknown location');
                    setSelectedPlace({
                        formatted_address: 'Unknown location',
                        geometry: { location: position },
                        address_components: []
                    });
                }
            });
        });

        // Add click event on map
        newMap.addListener('click', (event) => {
            const clickedLocation = {
                lat: event.latLng.lat(),
                lng: event.latLng.lng()
            };

            // Update marker position
            newMarker.setPosition(clickedLocation);
            setLocation(clickedLocation);
            setPendingSelection(true);

            // Get address from coordinates using the local geocoder instance
            newGeocoder.geocode({ location: clickedLocation }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    setAddress(results[0].formatted_address);
                    setCurrentEditAddress(results[0].formatted_address);
                    setSelectedPlace(results[0]);
                } else {
                    setAddress('Unknown location');
                    setCurrentEditAddress('Unknown location');
                    setSelectedPlace({
                        formatted_address: 'Unknown location',
                        geometry: { location: event.latLng },
                        address_components: []
                    });
                }
            });
        });

        // Initialize services
        setMap(newMap);
        setMarker(newMarker);
        setAutocompleteSuggestion(new window.google.maps.places.AutocompleteService());
        setPlacesService(new window.google.maps.places.PlacesService(newMap));

        // Set initial location

        if (defaultLocation)
            setLocation(defaultLocation);

        // // Get initial address if available using the local geocoder instance
        // newGeocoder.geocode({ location: defaultLocation }, (results, status) => {
        //     if (status === 'OK' && results && results[0]) {

        //         let state = results[0].address_components.find(comp => comp.types.includes('administrative_area_level_1'));
        //         setMatchState(state);
        //         let county = results[0].address_components.find(comp => comp.types.includes('administrative_area_level_2'));
        //         setMatchCounty(county);

        //         setAddress(results[0].formatted_address);
        //         setCurrentEditAddress(results[0].formatted_address);
        //     }
        // });

    }, [defaultLocation, skipInitialMap]);

    // Update marker and map when location changes
    useEffect(() => {

        if (!map || !marker) return;

        marker.setPosition(location);
        map.panTo(location);
    }, [location, map, marker]);

    // Get address suggestions from Google Places API
    const getSuggestions = useCallback(async (input) => {
        if (!input || !autocompleteSuggestion) return;

        setIsLoading(true);

        try {
            autocompleteSuggestion.getPlacePredictions(
                { input, types: ['geocode', 'establishment'] },
                (predictions, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                        setSuggestions(predictions);
                        setShowSuggestions(true);
                    } else {
                        setSuggestions([]);
                    }
                    setIsLoading(false);
                }
            );
        } catch (error) {
            console.error('Error fetching place suggestions:', error);
            setIsLoading(false);
        }
    }, [autocompleteSuggestion]);

    // Debounce function for input
    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func(...args), delay);
        };
    };

    // Debounced suggestion fetch
    const debouncedGetSuggestions = useMemo(
        () => debounce((input) => {
            getSuggestions(input);
        }, 300),
        [getSuggestions]
    );

    // Handle input change
    const handleInputChange = (e) => {
        const value = e.target.value;

        setIsChangingLocation(false);
        setAddress(value);

        setCurrentEditAddress(value);

        // Reset pending selection when input changes
        setPendingSelection(false);

        if (value.trim()) {
            debouncedGetSuggestions(value);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    // Handle place selection from suggestions
    const handleSelectPlace = (placeId) => {
        if (!placesService || !placeId) return;

        placesService.getDetails(
            { placeId, fields: ['geometry', 'formatted_address', 'address_components'] },
            (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry) {
                    const newLocation = {
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng()
                    };

                    // Update location and map
                    setLocation(newLocation);
                    setAddress(place.formatted_address || place.name);
                    setCurrentEditAddress(place.formatted_address || place.name);
                    setSuggestions([]);
                    setShowSuggestions(false);

                    // Set pending selection to true
                    setPendingSelection(true);
                    setSelectedPlace(place);

                    // Create marker if it doesn't exist
                    if (map && !marker) {
                        const newMarker = new window.google.maps.Marker({
                            position: newLocation,
                            map: map,
                            draggable: true,
                            animation: window.google.maps.Animation.DROP
                        });

                        // Add marker drag event
                        newMarker.addListener('dragend', () => {
                            const position = newMarker.getPosition();
                            const draggedLocation = {
                                lat: position.lat(),
                                lng: position.lng()
                            };
                            setLocation(draggedLocation);
                            setPendingSelection(true);

                            // Get address data from coordinates
                            const geocoder = new window.google.maps.Geocoder();
                            geocoder.geocode({ location: draggedLocation }, (results, status) => {
                                if (status === 'OK' && results && results[0]) {
                                    setAddress(results[0].formatted_address);
                                    setCurrentEditAddress(results[0].formatted_address);
                                    setSelectedPlace(results[0]);
                                } else {
                                    setAddress('Unknown location');
                                    setCurrentEditAddress('Unknown location');
                                    setSelectedPlace({
                                        formatted_address: 'Unknown location',
                                        geometry: { location: position },
                                        address_components: []
                                    });
                                }
                            });
                        });

                        setMarker(newMarker);
                    }

                    // Animate to the location
                    if (map) {
                        map.panTo(newLocation);
                        map.setZoom(16);

                        // Update marker position if it exists
                        if (marker) {
                            marker.setPosition(newLocation);
                        }
                    }
                }
            }
        );
    };

    // Handle confirming the selected location
    const handleConfirmLocation = () => {
        if (selectedPlace) {

            onLocationSelect(location, selectedPlace);
            setPendingSelection(false);
        }
        else {
            onLocationSelect(location, {
                formatted_address: address || 'Unknown location',
                geometry: { location: new window.google.maps.LatLng(location.lat, location.lng) },
                address_components: []
            });
            setPendingSelection(false);
        }
    };

    // Handle reset/cancel
    const handleReset = () => {

        setAddress('');
        setCurrentEditAddress('');
        setLocation(null);
        setPendingSelection(false);
        setSelectedPlace(null);
        setSuggestions([]);
        setShowSuggestions(false);

        // Reset map to initial state
        // if (map && marker) {
        //     if (skipInitialMap) {
        // If skipInitialMap is true, remove marker and center map to default position
        marker.setMap(null);
        setMarker(null);
        map.setCenter(defaultPosition);
        map.setZoom(4);
        //     } else {
        //         // Reset to default location
        //         const resetLocation = defaultLocation || defaultPosition;
        //         marker.setPosition(resetLocation);
        //         map.panTo(resetLocation);
        //         map.setZoom(15);
        //         setLocation(resetLocation);
        //     }
        // }
    };

    // Get current location using Geolocation API


    // Handle clicking outside suggestions to close them
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getAddress = () => {
        if (currentEditAddress && !isReset) {
            return currentEditAddress;
        }
        return address;
    };

    return (
        <div className="find-location-container">
            <div className="search-container">
                <div className="input-wrapper">
                    <input
                        ref={inputRef}
                        type="text"
                        value={getAddress()}
                        onChange={handleInputChange}
                        placeholder={placeholder}
                        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                        className="location-input"
                    />
                    {isLoading && <div className="loader"></div>}

                    {/* Clear button */}
                    {address && (
                        <button
                            className="clear-btn"
                            onClick={handleReset}
                        >
                            ×
                        </button>
                    )}
                </div>

                {/* Current Location Button */}
                {/* <div className="current-location-section">
                    <button
                        className={`current-location-btn ${isGettingCurrentLocation ? 'loading' : ''}`}
                        onClick={getCurrentLocation}
                        disabled={isGettingCurrentLocation}
                        type="button"
                    >
                        {isGettingCurrentLocation ? (
                            <>
                                <i className="fa fa-spinner fa-spin"></i>
                                <span>Getting location...</span>
                            </>
                        ) : (
                            <>
                                <i className="fa fa-location-arrow"></i>
                                <span>Use Current Location</span>
                            </>
                        )}
                    </button>
                </div> */}

                {/* Instructions */}
                <div className="location-instructions">
                    {pendingSelection ?
                        language.findLocation?.reviewInstructions || "Review the selected location and click 'Confirm Location' when ready. Click on the red marker and move to the appropriate location if needed." :
                        language.findLocation?.instructions || "Search for a location to get started"}
                </div>

                {/* Suggestions dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                    <ul ref={suggestionsRef} className="suggestions-list">
                        {suggestions.map((suggestion) => (
                            <li
                                key={suggestion.place_id}
                                onClick={() => handleSelectPlace(suggestion.place_id)}
                            >
                                <i className="fa fa-map-marker-alt"></i>
                                <span>{suggestion.description}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Map container */}
            <div
                ref={mapRef}
                className="map-container"
                style={{ height }}
            >

            </div>

            {/* Coordinates and action buttons */}
            <div className="location-footer">
                <div className="coordinates-display">
                    <div className="coordinate">
                        <span className="coordinate-label">Latitude:</span>
                        <span className="coordinate-value">
                            {location?.lat ? parseFloat(location.lat).toFixed(6) : ''}
                        </span>
                    </div>
                    <div className="coordinate">
                        <span className="coordinate-label">Longitude:</span>
                        <span className="coordinate-value">
                            {location?.lng ? parseFloat(location.lng).toFixed(6) : ''}
                        </span>
                    </div>
                </div>

                {/* Confirmation buttons */}
                {location && (
                    <div className="confirmation-buttons">
                        {isChangingLocation && (
                            <button className="confirm-btn" onClick={handleConfirmLocation}>
                                <i className="fa fa-check"></i>
                                {language.findLocation?.confirmLocation || "Confirm Location"}
                            </button>
                        )}
                        <button className="cancel-btn" onClick={handleReset}>
                            <i className="fa fa-times"></i>
                            {language.buttons?.clear || "Clear"}
                        </button>

                    </div>
                )}
            </div>
        </div>
    );
};

export default FindLocation;